import { Route, Routes, Navigate, useNavigate } from "react-router-dom";
import { NotFoundPage } from "dash-ui";

import SignIn from "pages/authentication/SignInPage/SignInPage";
import ForgotPasswordPage from "pages/authentication/ForgotPasswordPage/ForgotPasswordPage";
import ResetPasswordPage from "pages/authentication/ResetPasswordPage/ResetPasswordPage";
import OrganizationsPage from "pages/organizations/OrganizationsPage/OrganizationsPage";
import OrganizationDetailsPage from "pages/organizations/OrganizationDetailsPage/OrganizationDetailsPage";
import PropertiesPage from "pages/properties/PropertiesPage/PropertiesPage";
import PropertyDetailsPage from "pages/properties/PropertyDetailsPage/PropertyDetailsPage";
import PropertyPreviewPage from "pages/properties/PropertyPreviewPage/PropertyPreviewPage";
import PropertyMediaFolderPage from "pages/properties/PropertyMediaFolderPage/PropertyMediaFolderPage";
import AmenitiesPage from "pages/amenities/AmenitiesPage/AmenitiesPage";
import BrokersPage from "pages/brokers/BrokersPage/BrokersPage";
import BrokerDetailsPage from "pages/brokers/BrokerDetailsPage/BrokerDetailsPage";
import ExternalSaleTeamsPage from "pages/external-sales/external-sale-teams/ExternalSaleTeamsPage";
import ExternalSalesUsers from "pages/external-sales/external-sales-users/ExternalSalesUsers";
import ManagementPage from "pages/management/ManagementPage";
import AccountPage from "pages/account/AccountPage";

import { RoutesPath } from "flow-components/routes/routes";
import ProtectedRout from "flow-components/routes/ProtectedRout/ProtectedRout";
import useInitViewer from "common/hooks/useInitViewer/useInitViewer";
import { useBrowserTabTitle } from "common/hooks/use-browser-tab-title/use-browser-tab-title";
import PrivacyPolicy from "pages/flow/PrivacyPolicy/PrivacyPolicy";
import TermConditions from "pages/flow/TermConditions/TermConditions";
import MarketAreasPage from "pages/market-areas/MarketAreasPage/MarketAreasPage";
import { SignInPage } from "v2/pages/sign-in-page";
import { MfaConfirmationPage } from "v2/pages/mfa-confirmation-page";
import { PropertyPublicPreviewPage } from "v2/pages/properties/property-public-preview";
import ApiManagementPage from "pages/api-management/ApiManagementPage";
import ApiCompanyDetailsPage from "pages/api-management/ApiCompanyDetailsPage/ApiCompanyDetailsPage";
import ApiPluginUserDetailsPage from "pages/api-management/ApiPluginUserDetailsPage/ApiPluginUserDetailsPage";

function App() {
  const [isViewerReady] = useInitViewer();
  const navigate = useNavigate();
  useBrowserTabTitle();

  return isViewerReady ? (
    <Routes>
      <Route
        path="/"
        element={
          <ProtectedRout>
            <Navigate replace to={RoutesPath.Properties} />
          </ProtectedRout>
        }
      />

      <Route
        path={RoutesPath.Organizations}
        element={
          <ProtectedRout>
            <OrganizationsPage />
          </ProtectedRout>
        }
      />
      <Route
        path={RoutesPath.OrganizationDetails}
        element={
          <ProtectedRout>
            <OrganizationDetailsPage />
          </ProtectedRout>
        }
      />
      <Route
        path={RoutesPath.Amenities}
        element={
          <ProtectedRout>
            <AmenitiesPage />
          </ProtectedRout>
        }
      />
      <Route
        path={RoutesPath.Properties}
        element={
          <ProtectedRout>
            <PropertiesPage />
          </ProtectedRout>
        }
      />
      <Route
        path={RoutesPath.ExternalSales}
        element={
          <ProtectedRout>
            <ExternalSaleTeamsPage />
          </ProtectedRout>
        }
      />

      <Route
        path={RoutesPath.ExternalSalesUsers}
        element={
          <ProtectedRout>
            <ExternalSalesUsers />
          </ProtectedRout>
        }
      />
      <Route
        path={RoutesPath.PropertyDetails}
        element={
          <ProtectedRout stickyBreadcrumbs>
            <PropertyDetailsPage />
          </ProtectedRout>
        }
      />
      <Route
        path={RoutesPath.PropertyPreview}
        element={
          <ProtectedRout withContainer={false}>
            <PropertyPreviewPage />
          </ProtectedRout>
        }
      />
      <Route
        path={RoutesPath.PropertyMediaFolder}
        element={
          <ProtectedRout stickyBreadcrumbs>
            <PropertyMediaFolderPage />
          </ProtectedRout>
        }
      />
      <Route
        path={RoutesPath.Brokers}
        element={
          <ProtectedRout>
            <BrokersPage />
          </ProtectedRout>
        }
      />
      <Route
        path={RoutesPath.BrokersDetails}
        element={
          <ProtectedRout>
            <BrokerDetailsPage />
          </ProtectedRout>
        }
      />
      {/* <Route path={RoutesPath.SignIn} element={<SignIn />} /> */}
      <Route path={RoutesPath.SignIn} element={<SignInPage />} />
      <Route path={RoutesPath.SignInMFA} element={<MfaConfirmationPage />} />
      <Route
        path={RoutesPath.ForgotPassword}
        element={<ForgotPasswordPage />}
      />
      <Route path={RoutesPath.ResetPassword} element={<ResetPasswordPage />} />
      <Route
        path={RoutesPath.Management}
        element={
          <ProtectedRout>
            <ManagementPage />
          </ProtectedRout>
        }
      />
      <Route
        path={RoutesPath.APIManagement}
        element={
          <ProtectedRout>
            <ApiManagementPage />
          </ProtectedRout>
        }
      />
      <Route
        path={RoutesPath.APIManagementCompanyDetails}
        element={
          <ProtectedRout>
            <ApiCompanyDetailsPage />
          </ProtectedRout>
        }
      />
      <Route
        path={RoutesPath.APIManagementPluginUserDetails}
        element={
          <ProtectedRout>
            <ApiPluginUserDetailsPage />
          </ProtectedRout>
        }
      />
      <Route
        path={RoutesPath.Account}
        element={
          <ProtectedRout>
            <AccountPage />
          </ProtectedRout>
        }
      />
      <Route
        path={RoutesPath.MarketAreas}
        element={
          <ProtectedRout>
            <MarketAreasPage />
          </ProtectedRout>
        }
      />

      <Route path={RoutesPath.TermConditions} element={<TermConditions />} />
      <Route path={RoutesPath.PrivacyPolicy} element={<PrivacyPolicy />} />

      <Route
        path="*"
        element={
          <NotFoundPage onRedirect={() => navigate(RoutesPath.Properties)} />
        }
      />

      <Route
        path={RoutesPath.PropertyPublicPreview}
        element={<PropertyPublicPreviewPage />}
      />
    </Routes>
  ) : null;
}

export default App;
